import { axiosInstance } from "./axios";
import { IUser } from "dto/IUser";
import { IWorkspaceCompany } from "dto/IWorkspace";
import { IError } from "dto/IError";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { IUIPrivileges } from "dto/IUIPrivileges";
import usePrivilegesStore from "hooks/usePrivilegesStore";

const queryKeys = {
  getMe: (token: string) => ["userService.getMe", token],
  getPrivileges: () => ["userService.getPrivileges"],
};

const getMe = (): Promise<{ data: IUser }> => {
  return axiosInstance.get("/account");
};

const useGetMe = (token: string) => {
  return useCustomQuery(queryKeys.getMe(token), () => getMe(), {
    enabled: false,
    cacheTime: 0,
    staleTime: 0,
  });
};

const getPrivileges = (): Promise<{ data: IUIPrivileges }> => {
  return axiosInstance.get("/ui/privileges");
};

const useGetPrivileges = () => {
  return useCustomMutation(getPrivileges);
};

/**
 * Pobiera /ui/privileges i zasila store uprawnień: moduły pakietu, funkcje aplikacji
 * (scalenie pakietu z metryką pracownika), samą metrykę i przełącznik płatności instancji.
 * Po zalogowaniu (App) oraz po zmianie własnej metryki — żeby menu przeliczyło się bez
 * przeładowania.
 */
export const loadUiPrivileges = () =>
  getPrivileges()
    .then((r) => {
      usePrivilegesStore
        .getState()
        .init(
          r.data.Modules,
          r.data.Functions ?? null,
          r.data.Access ?? null,
          r.data.PaymentsEnabled ?? true,
        );
    })
    .catch(() => {
      // Pierwsze wczytanie się nie udało — menu jak przed wprowadzeniem funkcji (bez modułów
      // pakietu, bez ograniczeń ról), zamiast pustego. Nieudane odświeżenie nie psuje stanu.
      const store = usePrivilegesStore.getState();
      if (!store.loaded) {
        store.init([], null, null);
      }
    });

export interface IUpdateCompany {
  Name: string;
  Address: string;
  City: string;
  ZipCode: string;
  Phone: string;
  Email: string;
  NIP: string;
  REGON: string;
}

const getCompany = (): Promise<{ data: IWorkspaceCompany }> => {
  return axiosInstance.get("/account/company");
};

const useGetCompany = () => {
  return useCustomQuery("account.getCompany", () => getCompany());
};

const updateCompany = (payload: IUpdateCompany): Promise<{ data: IError }> => {
  return axiosInstance.post("/account/company", payload);
};

const useUpdateCompany = () => {
  return useCustomMutation(updateCompany);
};

const removeAccount = (): Promise<{}> => {
  return axiosInstance.get("/account/remove");
};

const useRemoveAccount = () => {
  return useCustomMutation(removeAccount);
};

const removeAccountConfirm = (data: {
  userId: string;
  token: string;
}): Promise<{}> => {
  return axiosInstance.get(`/account/confirm/${data.userId}/${data.token}`);
};

const useRemoveAccountConfirm = () => {
  return useCustomMutation(removeAccountConfirm);
};

export default {
  useGetMe,
  useUpdateCompany,
  useGetCompany,
  useGetPrivileges,
  useRemoveAccount,
  useRemoveAccountConfirm,
};
